import { Injectable } from '@angular/core';
import type { EnvioOk } from '../models/remittance.model';
import { esRechazoPorTasa } from 'src/app/shared/utils/error-http-real';
import { firstValueFrom } from 'rxjs';
import { BinanceDepositService } from '../../../services/binance-deposit.service';
import {
  esErrorAccionable,
  OthersPayoutsService,
} from '../../../services/others-payouts.service';
import { PeruPayoutsService } from '../../../services/peru-payouts.service';
import { TransaccionService } from '../../../services/transaccion.service';
import { WalletVesClientService } from '../../../services/wallet-ves-client.service';
import {
  AjusteTasaHonrada,
  BeneficiarioVM,
  CotizacionVM,
  ResultadoEnvio,
  SnapshotCotizacion,
} from '../models/remittance.model';
import * as R from '../rules/remittance-rules';
import { RemittanceQuoteService } from './remittance-quote.service';

/** Todo lo que hace falta para ejecutar un envío ya confirmado por el usuario. */
export interface IntencionEnvio {
  usuarioId: number;
  /** Cotización que el usuario vio y confirmó. */
  cotizacion: CotizacionVM;
  beneficiario: BeneficiarioVM;
  rielCo: 'COBREB' | 'CO';
  /** El usuario ya aceptó explícitamente un posible duplicado. */
  confirmarDuplicado?: boolean;
}

/**
 * Qué salió de verificar la tasa antes de despachar. Los dos campos son
 * independientes: `bloqueo` corta el envío, `ajuste` sólo informa que se envió
 * honrando lo que el usuario vio.
 */
export interface VerificacionTasa {
  /** Algo impide enviar: hay que reconfirmar, o no se pudo verificar. */
  bloqueo: ResultadoEnvio | null;
  /** Se honró la tasa mostrada en vez de la vigente. Sólo CLP→VES. */
  ajuste: AjusteTasaHonrada | null;
}

/** UUID v4 para la idempotencia de los rieles Vita. Fallback para WebViews viejos. */
function nuevoRequestId(): string {
  const c: any = typeof crypto !== 'undefined' ? crypto : null;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (ch) => {
    const r = (Math.random() * 16) | 0;
    return (ch === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

/**
 * Ejecuta el envío de una remesa ya cotizada y confirmada.
 *
 * Dos garantías que este servicio centraliza para TODOS los rieles — hoy sólo
 * las tienen los de Vita:
 *
 *  1. **Anti-desfase de tasa.** Antes de enviar re-cotiza y compara contra lo
 *     que el usuario confirmó. Si cambió, NO envía: devuelve `CambioDeTasa`
 *     para que el usuario vuelva a confirmar con los valores nuevos.
 *
 *  2. **No empeorar el riesgo de duplicado.** Los rieles propios NO tienen
 *     deduplicación en backend, así que acá NO se reintenta automáticamente.
 *     Ante un fallo ambiguo se devuelve `ambiguo: true` y la UI debe verificar
 *     en el historial antes de ofrecer reintentar.
 *
 * NO toca backend: usa los mismos endpoints de envío que las pantallas vigentes.
 */
@Injectable({ providedIn: 'root' })
export class RemittanceSubmitService {
  /**
   * Una intención de envío = un `requestId`. Se regenera tras un envío
   * exitoso y al confirmar un duplicado; se REUSA al re-confirmar tras un
   * cambio de tasa (sigue siendo la misma intención).
   */
  private requestId = nuevoRequestId();

  /**
   * Ventana del control de repetición. Dentro de este lapso, un envío
   * idéntico exige confirmación explícita del usuario.
   */
  private readonly VENTANA_DUPLICADO_MS = 10 * 60 * 1000;

  /** Clave de `sessionStorage` donde viven los envíos recientes. */
  private readonly STORE_ENVIOS = 'aguacate.remesas.enviosRecientes';

  constructor(
    private quote: RemittanceQuoteService,
    private othersPayouts: OthersPayoutsService,
    private peruPayouts: PeruPayoutsService,
    private transaccion: TransaccionService,
    private walletVes: WalletVesClientService,
    private binanceDeposit: BinanceDepositService,
  ) {}

  /** Foto de la cotización en el momento de confirmar. */
  tomarSnapshot(c: CotizacionVM): SnapshotCotizacion | null {
    if (c.recibe == null || c.tasaEfectiva == null) return null;
    return {
      origen: c.origen,
      destino: c.destino,
      monto: c.monto,
      recibe: c.recibe,
      tasaEfectiva: c.tasaEfectiva,
      tomadoEn: Date.now(),
      // Los ids viajan en la foto porque son parte de lo que se honra: sin
      // ellos el trío que valida el backend no cierra.
      tasaCambioId: c.tasaCambioId ?? null,
      valorCambioId: c.valorCambioId ?? null,
      cotizadoEn: c.cotizadoEn,
    };
  }

  /**
   * Re-cotiza y compara con lo confirmado. Es la protección contra cambio de
   * tasa que funciona en TODOS los rieles, sin backend nuevo.
   *
   * Dos comportamientos, según el corredor:
   *
   *  · **CLP→VES** — decide el SIGNO del movimiento (`R.decisionTasa`). Si
   *    honrar lo mostrado favorece a Aguacate o empata, se envía en silencio
   *    con lo que el usuario vio y se devuelve un `ajuste`. Si va en contra,
   *    se pide reconfirmar.
   *
   *  · **Los otros nueve pares** — `cambioSignificativo`, simétrico: cualquier
   *    variación por encima de 0,01 % pide reconfirmación. En los rieles de
   *    Vita no hay alternativa: el precio lo valida el proveedor.
   */
  async verificarTasa(
    snapshot: SnapshotCotizacion,
    rielCo: 'COBREB' | 'CO' = 'COBREB',
  ): Promise<VerificacionTasa> {
    const fresca = await this.cotizarParaVerificar(snapshot, rielCo);

    if (!fresca.disponible || fresca.recibe == null) {
      return {
        bloqueo: {
          ok: false,
          mensaje:
            fresca.motivo || 'No pudimos confirmar la tasa. Intenta nuevamente.',
          contactarOperador: false,
          ambiguo: false,
        },
        ajuste: null,
      };
    }

    const reconfirmar = R.aplicaHonrarTasa(snapshot.origen, snapshot.destino)
      ? R.decisionTasa(snapshot.recibe, fresca.recibe) === 'reconfirmar'
      : R.cambioSignificativo(snapshot.recibe, fresca.recibe);

    if (reconfirmar) {
      return {
        bloqueo: {
          cambio: true,
          mensaje:
            'La tasa cambió mientras confirmabas. Revisa el nuevo monto antes de enviar.',
          recibeAnterior: snapshot.recibe,
          cotizacion: fresca,
        },
        ajuste: null,
      };
    }

    // Se envía con la cotización del snapshot, no con la fresca. En CLP→VES eso
    // es honrar la tasa mostrada; en los demás rieles la diferencia es ruido de
    // redondeo y el resultado es el mismo.
    const ajuste = R.aplicaHonrarTasa(snapshot.origen, snapshot.destino)
      ? this.ajusteHonrado(snapshot, fresca.recibe)
      : null;

    return { bloqueo: null, ajuste };
  }

  /**
   * Re-cotiza para verificar, con UN reintento.
   *
   * Reintentar acá no roza la prohibición de reintentar envíos: esa regla
   * existe porque los rieles propios no deduplican ESCRITURAS. Esto es una
   * cotización —un GET— y todavía no se despachó nada. Cubre el 500 o el
   * timeout pasajero del servicio de tasas, que es el caso frecuente; si el
   * servicio está realmente caído el segundo intento también falla y su motivo
   * es el que se le muestra al usuario.
   */
  private async cotizarParaVerificar(
    snapshot: SnapshotCotizacion,
    rielCo: 'COBREB' | 'CO',
  ): Promise<CotizacionVM> {
    const primera = await this.quote.cotizar(
      snapshot.origen,
      snapshot.destino,
      snapshot.monto,
      rielCo,
    );
    if (primera.disponible && primera.recibe != null) return primera;

    return this.quote.cotizar(
      snapshot.origen,
      snapshot.destino,
      snapshot.monto,
      rielCo,
    );
  }

  /** Registro de que se envió honrando lo mostrado. Ver `AjusteTasaHonrada`. */
  private ajusteHonrado(
    s: SnapshotCotizacion,
    recibeVigente: number,
  ): AjusteTasaHonrada {
    return {
      origen: s.origen,
      destino: s.destino,
      monto: s.monto,
      recibeMostrado: s.recibe,
      recibeVigente,
      costoVes: R.costoDeHonrarVes(s.recibe, recibeVigente),
      costoPct: R.costoDeHonrarPct(s.recibe, recibeVigente),
      tasaCambioId: s.tasaCambioId ?? null,
      cotizadoEn: s.cotizadoEn ?? null,
      confirmadoEn: s.tomadoEn,
    };
  }

  /**
   * Envía. Asume que `verificarTasa` ya corrió y no detectó cambios: la
   * cotización de `intencion` es la vigente.
   */
  async enviar(intencion: IntencionEnvio): Promise<ResultadoEnvio> {
    const { cotizacion: c } = intencion;

    if (c.recibe == null || c.tasaEfectiva == null) {
      return this.error('No hay una cotización vigente para enviar.');
    }
    if (!c.activo) {
      return this.error(
        c.motivo || 'Este destino no acepta envíos en este momento.',
      );
    }

    // ESPEJO DE LA VALIDACIÓN DEL BACKEND, antes de despachar.
    //
    // `POST transaccion` responde 200 y encola; la validación de la tasa corre
    // después, en el consumidor de SQS (`transaction.service.ts:1301-1309`), y
    // si el trío no cierra hace `return` sin crear nada NI AVISAR. El cliente
    // vería «Transacción creada» y no existiría.
    //
    // Este riel es el único que manda a propósito una tasa que puede no ser la
    // vigente, así que el chequeo va acá: fallar visible es infinitamente mejor
    // que un envío descartado en silencio.
    if (R.aplicaHonrarTasa(c.origen, c.destino)) {
      const trioOk =
        c.tasaCambioId != null &&
        R.trioCoherente(c.monto, c.tasaEfectiva, c.recibe);
      if (!trioOk) {
        return this.error(
          'No pudimos confirmar la tasa de este envío. Vuelve a cotizar.',
        );
      }
    }

    // CONTROL DE REPETICIÓN (front). Los rieles propios no tienen dedupe en
    // backend, así que un segundo envío idéntico se crearía sin más. Acá se
    // exige confirmación explícita antes de repetir.
    //
    // No es una garantía —dos pestañas o dos dispositivos lo saltean, y hay
    // condición de carrera si dos envíos salen a la vez— pero cubre el caso
    // real: el usuario que reintenta porque no vio claro el resultado.
    const clave = this.claveEnvio(intencion);
    if (!intencion.confirmarDuplicado) {
      const previo = this.envioReciente(clave);
      if (previo) {
        return {
          ok: false,
          mensaje: `Hace un momento enviaste ${this.fmt(c.monto)} ${c.monedaOrigen} a este mismo beneficiario. Revisa tu historial antes de repetirlo.`,
          contactarOperador: false,
          ambiguo: false,
          posibleDuplicado: true,
        };
      }
    }

    try {
      const r = await this.despachar(intencion);
      if ((r as any).ok === true) {
        // Envío aceptado: se registra para el control de repetición y la
        // próxima intención necesita un id nuevo.
        this.registrarEnvio(clave);
        this.requestId = nuevoRequestId();
      }
      return r;
    } catch (e: any) {
      return this.traducirError(e, c.destino);
    }
  }

  // ── Control de repetición ──

  /** Identifica un envío por sus datos visibles: mismo par, monto y destinatario. */
  private claveEnvio(i: IntencionEnvio): string {
    const c = i.cotizacion;
    return [
      i.usuarioId,
      c.origen,
      c.destino,
      Math.round(c.monto * 100),
      i.beneficiario.id,
      c.destino === 'CO' ? i.rielCo : '',
    ].join('|');
  }

  /** Timestamp del último envío idéntico dentro de la ventana, o `null`. */
  private envioReciente(clave: string): number | null {
    const registros = this.leerEnvios();
    const ts = registros[clave];
    if (!ts) return null;
    return Date.now() - ts < this.VENTANA_DUPLICADO_MS ? ts : null;
  }

  private registrarEnvio(clave: string): void {
    const registros = this.leerEnvios();
    registros[clave] = Date.now();
    this.guardarEnvios(registros);
  }

  /**
   * Se persiste en `sessionStorage` para que el control sobreviva a una recarga
   * de la página — que es justo cuando el usuario duda y reintenta. De paso se
   * purgan los vencidos.
   */
  private leerEnvios(): Record<string, number> {
    try {
      const crudo = sessionStorage.getItem(this.STORE_ENVIOS);
      if (!crudo) return {};
      const parsed = JSON.parse(crudo) as Record<string, number>;
      const ahora = Date.now();
      const vigentes: Record<string, number> = {};
      for (const k of Object.keys(parsed)) {
        if (ahora - parsed[k] < this.VENTANA_DUPLICADO_MS) vigentes[k] = parsed[k];
      }
      return vigentes;
    } catch {
      return {};
    }
  }

  private guardarEnvios(registros: Record<string, number>): void {
    try {
      sessionStorage.setItem(this.STORE_ENVIOS, JSON.stringify(registros));
    } catch {
      // Sin sessionStorage el control degrada a memoria del tab; no bloquea.
    }
  }

  private fmt(n: number): string {
    return Math.round(n).toLocaleString('es-CL');
  }

  /** El usuario aceptó enviar pese al aviso de posible duplicado. */
  async confirmarDuplicado(intencion: IntencionEnvio): Promise<ResultadoEnvio> {
    // Es un segundo envío INTENCIONAL: con el id viejo el backend haría replay
    // del original y se tragaría este envío.
    this.requestId = nuevoRequestId();
    return this.enviar({ ...intencion, confirmarDuplicado: true });
  }

  // ───────────────────── Despacho por par ─────────────────────

  private async despachar(i: IntencionEnvio): Promise<ResultadoEnvio> {
    const c = i.cotizacion;
    const recibe = c.recibe as number;

    // ── Rieles Vita: idempotentes en backend ──
    if (c.destino === 'CO') {
      const base = {
        rail: i.rielCo,
        beneficiarioId: String(i.beneficiario.id),
        // El price-check va con el número TAL COMO vino del backend, sin
        // redondear: es lo que hace la pantalla vigente
        // (`vita-calculator-demo:727` manda `cotizacionClp.recibeCop` crudo) y
        // redondearlo puede no coincidir con lo que el backend espera.
        expectedRecibeCop: recibe,
        requestId: this.requestId,
        confirmarDuplicado: i.confirmarDuplicado,
      };
      const r =
        c.origen === 'CLP'
          ? await firstValueFrom(
              this.othersPayouts.enviarRemesaClp({ ...base, montoClp: c.monto }),
            )
          : c.origen === 'VES'
            ? await firstValueFrom(
                this.othersPayouts.enviarRemesaVes({ ...base, montoVes: c.monto }),
              )
            : await firstValueFrom(
                this.othersPayouts.enviarRemesaCo({ ...base, montoUsd: c.monto }),
              );
      return this.ok(r?.txId, r?.estado, r?.recibeCop ?? recibe, 'COP');
    }

    if (c.destino === 'PE') {
      const base = {
        beneficiarioId: String(i.beneficiario.id),
        expectedRecibePen: recibe,
        requestId: this.requestId,
        confirmarDuplicado: i.confirmarDuplicado,
      };
      const r =
        c.origen === 'CLP'
          ? await firstValueFrom(
              this.peruPayouts.enviarRemesaClp({ ...base, montoClp: c.monto }),
            )
          : c.origen === 'VES'
            ? await firstValueFrom(
                this.peruPayouts.enviarRemesaVes({ ...base, montoVes: c.monto }),
              )
            : await firstValueFrom(
                this.peruPayouts.enviarRemesa({ ...base, montoUsd: c.monto }),
              );
      return this.ok(r?.txId, r?.estado, r?.recibePen ?? recibe, 'PEN');
    }

    // ── Rieles propios: SIN idempotencia en backend, sin reintento ──
    if (c.destino === 'VE') {
      if (c.origen === 'USDT') {
        const r: any = await firstValueFrom(
          this.binanceDeposit.swapUsdtToVes({
            userId: String(i.usuarioId),
            usdtAmount: c.monto,
            vesRate: c.tasaEfectiva as number,
            vesAmount: recibe,
            cuentaBeneficiarioId: Number(i.beneficiario.id),
          } as any),
        );
        // Comprobante con lo realmente pagado (`swap.vesAmount`) y la tasa
        // aplicada (`swap.rate`); lo cotizado queda de respaldo. Esta respuesta
        // no trae estado: el de siempre.
        const pagado = Number(r?.swap?.vesAmount);
        const tasa = Number(r?.swap?.rate);
        return this.ok(null, 'POR_PROCESAR', pagado > 0 ? pagado : recibe, 'VES', tasa > 0 ? tasa : undefined);
      }

      // Con el error real: un 5xx sin mensaje queda incierto (sin reintento).
      const r: any = await this.transaccion.saveTransaccionConErrorReal({
        paisOrigen: 'CL',
        userId: i.usuarioId,
        totalMonedaOrigen: c.monto,
        totalMonedaDestino: recibe,
        monedaOrigen: 'CLP',
        monedaDestino: 'VES',
        paisDestino: 'VE',
        formaPago: 'WALLET',
        diferencia: null,
        tasa: c.tasaEfectiva,
        cuentaBeneficiarioId: Number(i.beneficiario.id),
        tasaCambioId: c.tasaCambioId,
        valorCambioId: c.valorCambioId,
        tipoTransaccion: 'FIAT',
      });
      return this.ok(r?.transaccionId ?? null, 'POR_PROCESAR', recibe, 'VES');
    }

    if (c.destino === 'CL') {
      if (c.origen === 'USDT') {
        const dto: any = {
          userId: String(i.usuarioId),
          usdtAmount: c.monto,
          clpAmount: recibe,
          tasa: c.tasaEfectiva,
          cuentaRetiroId: Number(i.beneficiario.id),
        };
        // Opcionales: sólo si la tasa los trae (espejo de ConvertUsdtClp).
        if (c.tasaCambioId != null) dto.tasaCambioId = c.tasaCambioId;
        if (c.valorCambioId != null) dto.valorCambioId = c.valorCambioId;

        const r: any = await firstValueFrom(
          this.binanceDeposit.retiroUsdtClpVesConErrorReal(dto),
        );
        return this.ok(null, r?.data?.estado ?? 'POR_PROCESAR', recibe, 'CLP');
      }

      const r: any = await firstValueFrom(
        this.walletVes.retiroVesClp({
          userId: Number(i.usuarioId),
          totalMonedaOrigen: c.monto,
          totalMonedaDestino: recibe,
          cuentaBeneficiarioId: Number(i.beneficiario.id),
          tasa: c.tasaEfectiva as number,
          tasaCambioId: c.tasaCambioId ?? undefined,
          valorCambioId: c.valorCambioId ?? undefined,
        }),
      );
      return this.ok(r?.data?.id ?? null, 'POR_PROCESAR', recibe, 'CLP');
    }

    return this.error('Destino no soportado.');
  }

  // ───────────────────── Errores ─────────────────────

  /**
   * Traduce el error a algo accionable.
   *
   * El caso delicado es el fallo transitorio (status 0 o 5xx) en un riel
   * PROPIO: el envío pudo haber llegado. No se reintenta ni se afirma que
   * falló — se marca `ambiguo` para que la UI verifique en el historial.
   */
  private traducirError(e: any, destino: string): ResultadoEnvio {
    // Vita: la tasa cambió entre el preview y el envío. No se creó nada.
    if (e?.cambioValores === true) {
      return {
        ok: false,
        mensaje:
          e.mensaje || 'La tasa cambió. Revisa el nuevo monto antes de enviar.',
        contactarOperador: false,
        ambiguo: false,
      };
    }

    // Vita: 409 accionables.
    if (esErrorAccionable(e)) {
      const aOperador =
        e.tipo === 'REMITENTE_DATOS_INCOMPLETOS' || e.tipo === 'DATOS_INCOMPLETOS';
      return {
        ok: false,
        mensaje: e.mensaje,
        contactarOperador: aOperador,
        ambiguo: false,
        posibleDuplicado: e.tipo === 'POSIBLE_DUPLICADO',
      };
    }

    const status = e?.status;

    // El servidor rechazó la tasa enviada. No se creó nada: se pide volver a
    // cotizar. Llega como:
    //  · VES→CLP: 409 "La tasa cambió o no es válida…";
    //  · swap USDT→VES: 400 "La tasa cambió mientras confirmabas…" (con o
    //    sin voseo, según la versión del back);
    //  · lambda `transaccion` (CLP→VES): 400 con `title` "Tasas de cambio
    //    actualizadas".
    if (esRechazoPorTasa(status, e?.message)) {
      return {
        ok: false,
        mensaje: 'La tasa cambió, vuelve a cotizar',
        contactarOperador: false,
        ambiguo: false,
        recotizar: true,
      };
    }

    // Cuando el servicio ya clasificó el error (`transitorio` booleano: los
    // rieles propios, ver `shared/utils/error-http-real.ts`) manda su
    // criterio: incierto sin conexión, timeout o cualquier 5xx (mueven dinero).
    // Los demás servicios no lo marcan: 0 o 5xx, como siempre.
    const transitorio =
      typeof e?.transitorio === 'boolean'
        ? e.transitorio
        : status === 0 || (status >= 500 && status <= 599);

    if (transitorio && !R.permiteReintentoAutomatico(destino as any)) {
      return {
        ok: false,
        mensaje:
          'No pudimos confirmar si tu envío se completó. Revisa tu historial ' +
          'antes de intentar de nuevo — no lo reenvíes sin verificar.',
        contactarOperador: false,
        ambiguo: true,
      };
    }

    return this.error(
      typeof e === 'string'
        ? e
        : e?.message || 'No se pudo completar el envío. Intenta nuevamente.',
    );
  }

  private ok(
    referencia: string | number | null | undefined,
    estado: string | undefined,
    recibe: number,
    moneda: string,
    tasaAplicada?: number,
  ): ResultadoEnvio {
    const ok: EnvioOk = {
      ok: true,
      referencia: referencia ?? null,
      estado: estado || 'POR_PROCESAR',
      recibe,
      monedaDestino: moneda,
    };
    if (tasaAplicada != null) ok.tasaAplicada = tasaAplicada;
    return ok;
  }

  private error(mensaje: string, ambiguo = false): ResultadoEnvio {
    return { ok: false, mensaje, contactarOperador: false, ambiguo };
  }
}
